import { Config } from 'config/envConfig';
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Form, Input, Select } from 'antd';
import { getUrl } from '../pdfDisplay/utils';
import {
    alert,
    AlertType,
    Button,
    LoadingView,
    Toggle,
} from '@quyva/shared-ui';
import { isEmailValid, isValidPort } from '@quyva/common';
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const { Option } = Select;

export const EmailInterface = ({
    repo,
    onClose,
    user,
    emailInterfaceList,
    getAllReposData,
}) => {
    const [formData, setFormData] = useState({
        email: '',
        password: '',
        mailProtocol: '',
        mailServerName: '',
        mailPort: '',
        smtpServerName: '',
        smtpPort: '',
        replyToEmail: '',
        isDisabled: false,
        restrictedAccess: false,
    });
    const [form] = Form.useForm();
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        const fetchEmailConfig = async () => {
            setIsLoading(true);
            try {
                const response = await axios.get(
                    getUrl(
                        `http://${Config.FAST_API_HOST}:${
                            Config.FAST_API_PORT
                        }/email_interface_routes/get_email_interface_info?user_id=${encodeURIComponent(
                            user.email
                        )}&repo_id=${repo.repoid}`
                    )
                );

                if (response.status === 200) {
                    const data = response.data;
                    if (data) {
                        const newFormData = {
                            email: data.email,
                            password: data.password,
                            mailProtocol: data.mail_protocol,
                            mailServerName: data.imap_pop_server,
                            mailPort: data.imap_pop_port,
                            smtpServerName: data.smtp_server,
                            smtpPort: data.smtp_port,
                            replyToEmail: data.reply_to_email || '',
                            isDisabled: data.is_deregistered,
                            restrictedAccess: data.restricted_access || false,
                        };
                        setFormData(newFormData);
                        form.setFieldsValue(newFormData);
                    }
                } else {
                    const errorData = response.error;
                    throw new Error(
                        errorData.error || 'Failed to load email configuration'
                    );
                }
            } catch (err) {
                setError(err.message || 'Failed to load email configuration');
            } finally {
                setIsLoading(false);
            }
        };
        fetchEmailConfig();
    }, [repo.repoid, user.email, form]);

    useEffect(() => {
        if (error) {
            alert(AlertType.ERROR, error);
        }
    }, [error]);

    const {
        email,
        password,
        mailProtocol,
        mailServerName,
        mailPort,
        smtpServerName,
        smtpPort,
        replyToEmail,
        isDisabled,
        restrictedAccess,
    } = formData;

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleSelectChange = (value, field) => {
        setFormData((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    const handleSwitchChange = (checked) => {
        setFormData((prev) => ({
            ...prev,
            isDisabled: checked,
        }));
    };

    const isFormComplete = () => {
        if (isDisabled) return true;

        const requiredFields = [
            email,
            password,
            mailProtocol,
            mailServerName,
            mailPort,
            smtpServerName,
            smtpPort,
        ];

        // All must be non-empty and defined
        return requiredFields.every(
            (field) =>
                field !== undefined &&
                field !== null &&
                String(field).trim() !== ''
        );
    };

    const onFinish = async () => {
        setError('');
        setIsSaving(true);
        try {
            const normalizedEmail = email.toLowerCase().trim();
            const repoEmail = repo.emailInterface?.toLowerCase().trim();
            const alreadyLinked = emailInterfaceList.includes(normalizedEmail);
            if (!isDisabled) {
                if (!isEmailValid(normalizedEmail)) {
                    setError('Please enter a valid email address.');
                    return;
                }
                if (alreadyLinked && normalizedEmail !== repoEmail) {
                    setError(
                        'Email already linked with one of your repositories.'
                    );
                    return;
                }
                if (!isValidPort(mailPort)) {
                    setError('Mail Port must be a valid port number');
                    return;
                }
                if (!isValidPort(smtpPort)) {
                    setError('SMTP Port must be a valid port number');
                    return;
                }
            }

            const formData = new FormData();
            formData.append('userId', user.email);
            formData.append('repoId', repo.repoid);
            formData.append('emailAddress', normalizedEmail);
            formData.append('password', password);
            formData.append('mailProtocol', mailProtocol);
            formData.append('imapPopServer', mailServerName);
            formData.append('imapPopPort', Number(mailPort));
            formData.append('smtpServer', smtpServerName);
            formData.append('smtpPort', Number(smtpPort));
            formData.append('replyToEmail', replyToEmail);
            formData.append('isServicesDisabled', isDisabled);
            formData.append('restrictedAccess', restrictedAccess);

            const response = await axios.post(
                getUrl(
                    `http://${Config.FAST_API_HOST}:${Config.FAST_API_PORT}/email_interface_routes/email-interface-info`
                ),
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                }
            );

            if (response.status === 200) {
                alert(AlertType.SUCCESS, 'Email Interface configuration saved');
                await getAllReposData();
            } else {
                const errorData = await response.error;
                throw new Error(
                    errorData.error || 'Failed to save email configuration'
                );
            }

            onClose();
        } catch (err) {
            // console.error('Error during email interface API call:', err);
            const errorMessage = err.response?.data?.detail || err.message;
            setError(errorMessage || 'Failed to save email configuration');
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className='w-[40vw] w-min-[40vw] flex-col p-4 rounded-xl bg-backgrounds-slate flex flex-col h-[calc(100vh-200px)]'>
            {(isLoading || isSaving) && (
                <div className='w-full h-full flex justify-center items-center'>
                    <LoadingView
                        loadingText={
                            isLoading
                                ? 'Loading email configuration'
                                : isSaving
                                ? 'Saving email configuration'
                                : ''
                        }
                    />
                </div>
            )}

            <div className='flex flex-col flex-1 overflow-hidden'>
                <div className='overflow-y-auto flex-1'>
                    <Form
                        form={form}
                        name='basic'
                        initialValues={formData}
                        onFinish={onFinish}
                        layout='vertical'
                    >
                        <Form.Item
                            label={
                                <>
                                    <span className='font-semibold text-texts-secondary300'>
                                        Email
                                    </span>
                                    <FontAwesomeIcon
                                        icon={faInfoCircle}
                                        className='ml-2 text-texts-tertiary200 cursor-pointer'
                                        title={`This mail will receive mails which is assumed to be related to ${repo.name} repository.`}
                                    />
                                </>
                            }
                            name='email'
                            rules={[
                                {
                                    required: !isDisabled,
                                    message: 'Please input your Mail!',
                                },
                            ]}
                        >
                            <Input
                                name='email'
                                onChange={(e) => {
                                    handleChange(e);
                                    form.setFieldsValue({
                                        email: e.target.value,
                                    });
                                }}
                                disabled={isDisabled}
                            />
                        </Form.Item>

                        <Form.Item
                            label={
                                <span className='font-semibold text-texts-secondary300'>
                                    Password
                                </span>
                            }
                            name='password'
                            rules={[
                                {
                                    required: !isDisabled,
                                    message: 'Please input your mail password!',
                                },
                            ]}
                        >
                            <Input.Password
                                name='password'
                                onChange={(e) => {
                                    handleChange(e);
                                    form.setFieldsValue({
                                        password: e.target.value,
                                    });
                                }}
                                disabled={isDisabled}
                            />
                        </Form.Item>

                        <Form.Item
                            label={
                                <span className='font-semibold text-texts-secondary300'>
                                    Mail Protocol
                                </span>
                            }
                            name='mailProtocol'
                            rules={[
                                {
                                    required: !isDisabled,
                                    message: 'Please select the Mail Protocol!',
                                },
                            ]}
                        >
                            <Select
                                onChange={(value) => {
                                    handleSelectChange(value, 'mailProtocol');
                                    form.setFieldsValue({
                                        mailProtocol: value,
                                    });
                                }}
                                placeholder='Select service type'
                                disabled={isDisabled}
                            >
                                <Option value='IMAP'>IMAP</Option>
                                <Option value='POP'>POP</Option>
                            </Select>
                        </Form.Item>

                        <Form.Item
                            label={
                                <>
                                    <span className='font-semibold text-texts-secondary300'>
                                        Mail Server
                                    </span>
                                    <FontAwesomeIcon
                                        icon={faInfoCircle}
                                        className='ml-2 text-texts-tertiary200 cursor-pointer'
                                        title={`IMAP / POP Mail Server`}
                                    />
                                </>
                            }
                            name='mailServerName'
                            rules={[
                                {
                                    required: !isDisabled,
                                    message: 'Please input the Mail Server!',
                                },
                            ]}
                        >
                            <Input
                                name='mailServerName'
                                onChange={(e) => {
                                    handleChange(e);
                                    form.setFieldsValue({
                                        mailServerName: e.target.value,
                                    });
                                }}
                                disabled={isDisabled}
                            />
                        </Form.Item>

                        <Form.Item
                            label={
                                <>
                                    <span className='font-semibold text-texts-secondary300'>
                                        Mail Port
                                    </span>
                                    <FontAwesomeIcon
                                        icon={faInfoCircle}
                                        className='ml-2 text-texts-tertiary200 cursor-pointer'
                                        title='Valid port range is 1 to 65535 (IANA standard)'
                                    />
                                </>
                            }
                            name='mailPort'
                            rules={[
                                {
                                    required: !isDisabled,
                                    message: 'Please input the Mail Port!',
                                },
                            ]}
                        >
                            <Input
                                name='mailPort'
                                onChange={(e) => {
                                    handleChange(e);
                                    form.setFieldsValue({
                                        mailPort: e.target.value,
                                    });
                                }}
                                disabled={isDisabled}
                                type='number'
                            />
                        </Form.Item>

                        <Form.Item
                            label={
                                <>
                                    <span className='font-semibold text-texts-secondary300'>
                                        SMTP Server
                                    </span>
                                    <FontAwesomeIcon
                                        icon={faInfoCircle}
                                        className='ml-2 text-texts-tertiary200 cursor-pointer'
                                        title={`Simple Mail Transfer Protocol Server.`}
                                    />
                                </>
                            }
                            name='smtpServerName'
                            rules={[
                                {
                                    required: !isDisabled,
                                    message: 'Please input the SMTP Server!',
                                },
                            ]}
                        >
                            <Input
                                name='smtpServerName'
                                onChange={(e) => {
                                    handleChange(e);
                                    form.setFieldsValue({
                                        smtpServerName: e.target.value,
                                    });
                                }}
                                disabled={isDisabled}
                            />
                        </Form.Item>

                        <Form.Item
                            label={
                                <>
                                    <span className='font-semibold text-texts-secondary300'>
                                        SMTP Port
                                    </span>
                                    <FontAwesomeIcon
                                        icon={faInfoCircle}
                                        className='ml-2 text-texts-tertiary200 cursor-pointer'
                                        title='Valid port range is 1 to 65535 (IANA standard)'
                                    />
                                </>
                            }
                            name='smtpPort'
                            rules={[
                                {
                                    required: !isDisabled,
                                    message: 'Please input the SMTP Port!',
                                },
                            ]}
                        >
                            <Input
                                name='smtpPort'
                                onChange={(e) => {
                                    handleChange(e);
                                    form.setFieldsValue({
                                        smtpPort: e.target.value,
                                    });
                                }}
                                disabled={isDisabled}
                                type='number'
                            />
                        </Form.Item>

                        <Form.Item
                            label={
                                <>
                                    <span className='font-semibold text-texts-secondary300'>
                                        Redirect Replies To
                                    </span>
                                    <FontAwesomeIcon
                                        icon={faInfoCircle}
                                        className='ml-2 text-texts-tertiary200 cursor-pointer'
                                        title={`If set, every automated reply sent from ${
                                            email ? email : 'the email set above'
                                        } will be redirected to this address instead of back to whoever originally wrote in. Leave blank to have replies go back to the original sender as usual.`}
                                    />
                                </>
                            }
                            name='replyToEmail'
                            rules={[
                                {
                                    required: false,
                                    message: 'Please enter a valid redirect email address!',
                                },
                            ]}
                        >
                            <Input
                                name='replyToEmail'
                                value={replyToEmail}
                                onChange={(e) => {
                                    handleChange(e);
                                    form.setFieldsValue({
                                        replyToEmail: e.target.value,
                                    });
                                }}
                                disabled={isDisabled}
                            />
                        </Form.Item>

                        <Form.Item>
                            <div className='flex items-center'>
                                <Toggle
                                    toggleText='Restrict to approved contacts only'
                                    toggleTextClassName='font-semibold text-texts-secondary300 mr-3'
                                    checked={restrictedAccess}
                                    onChange={(checked) => {
                                        setFormData((prev) => ({
                                            ...prev,
                                            restrictedAccess: checked,
                                        }));
                                        form.setFieldsValue({
                                            restrictedAccess: checked,
                                        });
                                    }}
                                />
                            </div>
                            <div className='text-xs text-texts-secondary300 mt-1'>
                                When on, this mailbox answers only senders in your
                                organisation&apos;s uploaded contacts list (Settings →
                                Organization → Access Control).
                            </div>
                        </Form.Item>
                    </Form>
                </div>

                <div className='mt-auto pt-4 border-t border-gray-200 bg-backgrounds-slate'>
                    <div className='flex justify-between space-x-4'>
                        <div className='flex justify-start items-center'>
                            <div className='flex items-center'>
                                <Toggle
                                    toggleText='Disable Email Interface'
                                    toggleTextClassName='font-semibold text-texts-secondary300 mr-3'
                                    checked={isDisabled}
                                    onChange={(checked) => {
                                        handleSwitchChange(checked);
                                        form.setFieldsValue({
                                            isDisabled: checked,
                                        });
                                    }}
                                />
                            </div>
                        </div>
                        <div className='flex justify-between items-center'>
                            <Button
                                type='danger'
                                onClick={onClose}
                                text='Cancel'
                                className='w-24 mr-8'
                                disabled={isSaving}
                            />
                            <Button
                                type='success'
                                onClick={onFinish}
                                className='w-24'
                                disabled={!isFormComplete() || isSaving}
                                text='Save'
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
